"use client";

import { GroupeSelectInput } from "@repo/types";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb } from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useGroupes } from "../../hooks/api/groupe";
import { useDebounce } from "../../hooks/use-debounce";
import { useListParams } from "../../hooks/use-list-params";
import { AuthService } from "../../lib/api/services/auth.service";
import { GroupeList } from "./groupe-list";

const STRIPPED_FILTER_KEY = "stripped";
const FILTER_KEYS = [STRIPPED_FILTER_KEY] as const;
type StrippedFilter = NonNullable<GroupeSelectInput["is_stripped"]>;
const STRIPPED_VALUES: readonly StrippedFilter[] = ["all", "stripped", "non-stripped"];

function toStrippedFilter(value: string | undefined): StrippedFilter {
  return (STRIPPED_VALUES as readonly string[]).includes(value ?? "")
    ? (value as StrippedFilter)
    : "all";
}

export function GroupeManagement() {
  const navigate = useNavigate();
  const {
    page,
    limit,
    search,
    sortBy,
    filters,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setFilter,
  } = useListParams({ filterKeys: FILTER_KEYS });

  const debouncedSearch = useDebounce(search, 300);
  const strippedFilter = toStrippedFilter(filters[STRIPPED_FILTER_KEY]);
  const isAdmin = useMemo(() => new AuthService().isAdmin(), []);

  const { data: groupesData, isLoading, error } = useGroupes({
    page,
    limit,
    sortBy: sortBy as "recent" | "oldest" | "alphanum",
    search: debouncedSearch || undefined,
    is_stripped: strippedFilter,
  });

  const goToList = () => navigate("/admin/droits/groupe");

  return (
    <div className="space-y-3">
      <div>
        <Breadcrumb
          items={[
            { label: "Gestion" },
            { label: "Groupes", current: true },
          ]}
          showHomeIcon={true}
          onHomeClick={() => navigate("/admin/dashboard")}
          className="mb-1"
        />
      </div>

      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold sm:text-2xl">Gestion des Groupes</h1>
        {isAdmin && (
          <Button onClick={() => navigate("/admin/droits/groupe/create")}>
            Créer un groupe
          </Button>
        )}
      </div>

      <Card className="p-3">
        <GroupeList
          groupes={groupesData?.data || []}
          isLoading={isLoading}
          error={error}
          canManage={isAdmin}
          onView={(id) => navigate(`/admin/droits/groupe/${id}`)}
          onEdit={(id) => navigate(`/admin/droits/groupe/${id}/edit`)}
          page={page}
          limit={limit}
          total={groupesData?.meta.total || 0}
          pageCount={groupesData?.meta.totalPages || 0}
          sortBy={sortBy}
          searchTerm={search}
          strippedFilter={strippedFilter}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onSearchChange={setSearch}
          onSortChange={setSort}
          onStrippedFilterChange={(value) => setFilter(STRIPPED_FILTER_KEY, value)}
        />
      </Card>
    </div>
  );
}

